import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ColumnEmitted, Tree } from '../../../column-picker/models/column-picker';
import { ColumnPickerComponent } from '../../../column-picker/column-picker.component';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { Subscription, switchMap, tap } from 'rxjs';
import { MetadataService } from '../../../../../../../../services/metadata.service';
import { MetadataColumn, MetadataFilter, MetadataFilterParameter } from '../../../../../../../../models/metadata';
import { DateRangeFilterComponent } from '../date-range-filter/date-range-filter.component';
import { TextFilterComponent } from '../text-filter/text-filter.component';
import { DateFilterComponent } from '../date-filter/date-filter.component';
import { NumberFilterComponent } from '../number-filter/number-filter.component';
import { TextArrayFilterComponent } from '../text-array-filter/text-array-filter.component';
import { AnyArrayFilterComponent } from '../any-array-filter/any-array-filter.component';
import { AnyFilterComponent } from '../any-filter/any-filter.component';
import { TabFilter } from '../../../../../../models/utils-tabs';
import { TableType } from '../../../../../../../../models/shipment';

@Component({
  selector: 'app-filter-item',
  standalone: true,
  templateUrl: './filter-item.component.html',
  imports: [
    ColumnPickerComponent,
    MatFormField,
    MatSelect,
    MatOption,
    MatLabel,
    ReactiveFormsModule,
    DateRangeFilterComponent,
    TextFilterComponent,
    DateFilterComponent,
    NumberFilterComponent,
    TextArrayFilterComponent,
    AnyArrayFilterComponent,
    AnyFilterComponent,
    MatError
  ],
  styleUrl: './filter-item.component.scss'
})
export class FilterItemComponent implements OnChanges, OnDestroy {
  @Input() treeNodes: Tree[] = [];
  @Input() filterGroup!: FormGroup;
  @Input() preselectedFilter?: TabFilter;
  @Input() type!: TableType;
  @Output() filterDeleted = new EventEmitter();

  availableFilters: Pick<MetadataColumn, 'name' | 'key' | 'allowedValues' | 'dataType'>[] = [];
  filterParams: MetadataFilterParameter[] | null = null;
  selectedColumn: MetadataColumn | undefined;

  private subscription = new Subscription();

  constructor(private metadataService: MetadataService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterGroup'] && changes['treeNodes']) {
      this.handleColumnChanges();
      this.handleFilterChanges();
    }
    if (changes['preselectedFilter']) {
      setTimeout(() => this.updateParametersControls());
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  paramsGroup() {
    return this.filterGroup.get('parameters') as FormGroup;
  }

  onColumnSelect({ key }: ColumnEmitted) {
    const control = this.filterGroup.get('columnKey');
    control?.setValue(key);
    control?.setErrors(null);
  }

  private handleColumnChanges() {
    this.subscription.add(
      this.filterGroup
        .get('columnKey')
        ?.valueChanges.pipe(
          tap(value => {
            this.filterKeyControl().reset();
            value ? this.filterKeyControl().enable() : this.filterKeyControl().disable();
          }),
          switchMap(columnKey =>
            this.metadataService.getMetadata().pipe(
              tap(({ shipmentColumns, containerColumns, orderColumns, orderExceptionColumns, filters }) => {
                const columnsConfig: { [key: string]: MetadataColumn[] } = {
                  Shipments: shipmentColumns,
                  Containers: containerColumns,
                  Orders: orderColumns,
                  OrderExceptions: orderExceptionColumns
                };
                const columns = columnsConfig[this.type];
                this.setAvailableFilters(columns, filters, columnKey);
              })
            )
          )
        )
        .subscribe()
    );
  }

  private handleFilterChanges() {
    this.subscription.add(
      this.filterGroup
        .get('filterKey')
        ?.valueChanges.pipe(
          switchMap(filterKey =>
            this.metadataService.getMetadata().pipe(
              tap(({ filters }) => {
                this.filterParams = filters.find(({ key }) => key === filterKey)?.parameters || null;
                this.recreateParamsControls();
              })
            )
          )
        )
        .subscribe()
    );
  }

  private setAvailableFilters(columns: MetadataColumn[], filters: MetadataFilter[], value: string) {
    this.selectedColumn = columns.find(column => column.key === value);
    this.availableFilters = filters
      .filter(({ key }) => this.selectedColumn?.filters?.includes(key))
      .map(({ key, name }) => ({
        key,
        name,
        allowedValues: this.selectedColumn?.allowedValues || [],
        dataType: this.selectedColumn?.dataType || 'Text'
      }));
    this.setDefaultFilter();
  }

  private setDefaultFilter() {
    if (this.selectedColumn?.allowedValues?.length) {
      this.filterKeyControl()?.setValue('equalsOneOf');
    }
  }

  private recreateParamsControls() {
    this.clearParamsGroup();
    if (this.filterParams) {
      if (this.filterParams.length === 1) {
        this.paramsGroup().addControl('value', new FormControl(null, Validators.required));
      } else if (this.filterParams.length === 2) {
        this.paramsGroup().addControl('from', new FormControl(null, Validators.required));
        this.paramsGroup().addControl('to', new FormControl(null, Validators.required));
      }
    }
  }

  private clearParamsGroup() {
    this.paramsGroup().reset();
    Object.keys(this.paramsGroup().controls).forEach(key => this.paramsGroup().removeControl(key));
  }

  private filterKeyControl() {
    return this.filterGroup.get('filterKey') as FormControl;
  }

  private updateParametersControls() {
    if (!this.preselectedFilter) {
      return;
    }
    const parametersGroup = this.filterGroup.get('parameters') as FormGroup;
    const { parameters } = this.preselectedFilter;
    if ('value' in parameters) {
      this.subscription.add(
        this.metadataService
          .getMetadata()
          .pipe(tap(() => parametersGroup.setControl('value', new FormControl(parameters.value, Validators.required))))
          .subscribe()
      );
    } else if ('from' in parameters && 'to' in parameters) {
      parametersGroup.setControl('from', new FormControl(parameters.from, Validators.required));
      parametersGroup.setControl('to', new FormControl(parameters.to, Validators.required));
    }
  }
}
